import { useState } from "react";
import { istBetrieb } from "./konsole-nav";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../data";
import { Dialog, PillView, useAction } from "../../components/ui";
import { t } from "../../lib/sprache";
import type { Pill } from "../../model/invoice-view";
import { languageLabel, shortWhen } from "./demo-termine-model";
import { useKonsolenRolle } from "./rolle";
import { useNavigate } from "react-router";
import { leadFromDemoRequest } from "./crm-data";

/**
 * Demo-Anfragen (0457, Inhaber 30.10.2026): Interessenten fragen auf creatoroffice.de/demo-anfrage eine Demo an; hier stehen die
 * Anfragen mit Status. „Termine schicken“ schickt den persönlichen Link zur Buchungsseite mit den nächsten freien Zeitfenstern,
 * nur auf Knopfdruck und mit Rückfrage. Bucht die Person danach, springt der Status von selbst auf „gebucht“.
 */
export type DemoAnfrageStatus = "open" | "slots_sent" | "booked" | "done";
export interface DemoAnfrage {
  id: string; name: string; email: string; company: string; website: string | null; creators: string | null; wish: string | null;
  message: string | null; language: string; source: string | null; status: DemoAnfrageStatus; created_at: string; updated_at: string;
  slots_sent_at: string | null; slots_sent_count: number; booking: { id: string; starts_at: string; cancelled: boolean } | null;
}
const parse = <T,>(r: unknown): T => (typeof r === "string" ? JSON.parse(r) : r) as T;
export const ANFRAGEN_KEY = ["platform_demo_requests", "alle", "betrieb"] as const;

export function anfrageStatus(a: Pick<DemoAnfrage, "status" | "booking">): Pill {
  switch (a.status) {
    case "open": return { label: t("Offen", "Open"), tone: "warning", symbol: "!" };
    case "slots_sent": return { label: t("Termine geschickt", "Times sent"), tone: "info", symbol: "→" };
    case "booked": return { label: a.booking?.cancelled ? t("Gebucht, abgesagt", "Booked, cancelled") : t("Gebucht", "Booked"), tone: a.booking?.cancelled ? "neutral" : "success", symbol: "✓" };
    default: return { label: t("Erledigt", "Done"), tone: "neutral", symbol: "○" };
  }
}

export function DemoAnfragen({ freieZeiten }: { freieZeiten: number }) {
  const q = useQuery({ queryKey: ANFRAGEN_KEY, queryFn: async () => parse<DemoAnfrage[]>(await api.read("platform_demo_requests", {})) ?? [] });
  const [schicken, setSchicken] = useState<DemoAnfrage | null>(null);
  const [alle, setAlle] = useState(false);
  const { busy, run } = useAction();
  const qc = useQueryClient();
  // Vertrieb-Konsole (0459): der Vertrieb liest Anfragen; Termine schicken und Status setzen bleiben beim Admin
  const admin = istBetrieb(useKonsolenRolle());
  const navigate = useNavigate();
  // 0460: zur Lead-Akte (vorhandener Lead über Anfrage oder Adresse, sonst neu); für Admin und Vertrieb
  const zumLead = (a: DemoAnfrage) => void run(`lead-${a.id}`, () => leadFromDemoRequest(api, a.id)).then((id) => { if (id) navigate(`/intern/leads/${id}`); });
  const liste = q.data ?? [];
  const offen = liste.filter((a) => a.status === "open").length;
  const sichtbar = alle ? liste : liste.filter((a) => a.status !== "done");
  const status = (a: DemoAnfrage, s: DemoAnfrageStatus) =>
    void run(`status-${a.id}`, async () => {
      await api.rpc("platform_demo_request_set_status", { p_request: a.id, p_status: s });
      await qc.invalidateQueries({ queryKey: ANFRAGEN_KEY });
    }, s === "done" ? t("Als erledigt markiert.", "Marked as done.") : t("Wieder offen.", "Open again."));

  return (
    <section className="stack" aria-labelledby="demo-anfragen-h">
      <div className="row between">
        <h2 id="demo-anfragen-h" className="tk-abschnitt">{t("Demo-Anfragen", "Demo requests")} · {offen} {t("offen", "open")}</h2>
        {liste.some((a) => a.status === "done") && <button type="button" onClick={() => setAlle(!alle)}>{alle ? t("Erledigte ausblenden", "Hide done") : t("Erledigte zeigen", "Show done")}</button>}
      </div>
      {q.error ? <p className="tk-error" role="alert">{t("Die Anfragen ließen sich nicht laden.", "The requests could not be loaded.")}</p>
        : q.isPending ? <p className="muted">{t("Wird geladen …", "Loading …")}</p>
          : sichtbar.length === 0 ? <div className="panel tk-empty"><p>{t("Keine offenen Anfragen. Neue Anfragen von creatoroffice.de/demo-anfrage stehen hier, dazu kommt eine Mail an hallo@.", "No open requests. New requests from creatoroffice.de/demo-anfrage appear here, plus an email to hallo@.")}</p></div>
            : (
              <ul className="tk-demo-karten">
                {sichtbar.map((a) => (
                  <li key={a.id} className="panel tk-demo-karte tk-demo-anfrage">
                    <div className="tk-demo-zeit">
                      <strong>{a.company}</strong>
                      <span>{t(`angefragt ${shortWhen(a.created_at)}`, `requested ${shortWhen(a.created_at)}`)}</span>
                      <PillView pill={anfrageStatus(a)} />
                    </div>
                    <dl className="tk-demo-fakten">
                      <div><dt>{t("Kontakt", "Contact")}</dt><dd>{a.name} · <a href={`mailto:${a.email}`}>{a.email}</a></dd></div>
                      {a.website && <div><dt>{t("Website/Instagram", "Website/Instagram")}</dt><dd>{a.website}</dd></div>}
                      <div><dt>{t("Anzahl Creator", "Creators")}</dt><dd>{a.creators ?? "–"}</dd></div>
                      {a.wish && <div><dt>{t("Wunschzeitraum", "Preferred time")}</dt><dd>{a.wish}</dd></div>}
                      {a.message && <div><dt>{t("Nachricht", "Message")}</dt><dd className="tk-umbruch">{a.message}</dd></div>}
                      <div><dt>{t("Sprache", "Language")}</dt><dd>{languageLabel(a.language)}{a.source ? ` · ${a.source}` : ""}</dd></div>
                      {a.slots_sent_at && <div><dt>{t("Termine geschickt", "Times sent")}</dt><dd>{shortWhen(a.slots_sent_at)}{a.slots_sent_count > 1 ? t(` (${a.slots_sent_count}×)`, ` (${a.slots_sent_count}×)`) : ""}</dd></div>}
                      {a.booking && <div><dt>{t("Gebucht für", "Booked for")}</dt><dd>{shortWhen(a.booking.starts_at)}</dd></div>}
                    </dl>
                    <div className="row tk-demo-aktionen">
                      <button type="button" disabled={!!busy} aria-busy={busy === `lead-${a.id}`} onClick={() => zumLead(a)}>{t("Lead öffnen", "Open lead")}</button>
                    {admin && <>
                      {/* nach einer abgesagten Buchung wieder Termine anbieten (Prüf-Chat D2) */}
                      {(a.status === "open" || a.status === "slots_sent" || (a.status === "booked" && a.booking?.cancelled)) && (
                        <button type="button" className={a.status === "open" ? "primary" : undefined} onClick={() => setSchicken(a)}>
                          {a.slots_sent_at ? t("Termine erneut schicken", "Send times again") : t("Termine schicken", "Send times")}
                        </button>
                      )}
                      {a.status !== "done"
                        ? <button type="button" disabled={!!busy} onClick={() => status(a, "done")}>{t("Erledigt", "Done")}</button>
                        : <button type="button" disabled={!!busy} onClick={() => status(a, "open")}>{t("Wieder öffnen", "Reopen")}</button>}
                    </>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
      <SchickenDialog anfrage={schicken} freieZeiten={freieZeiten} onClose={() => setSchicken(null)} />
    </section>
  );
}

function SchickenDialog({ anfrage, freieZeiten, onClose }: { anfrage: DemoAnfrage | null; freieZeiten: number; onClose: () => void }) {
  const { busy, error, run } = useAction();
  const qc = useQueryClient();
  const schicken = async () => {
    if (!anfrage) return;
    const r = await run("schicken", async () => {
      const x = parse<{ slots: number }>(await api.rpc("platform_demo_request_send_slots", { p_request: anfrage.id }));
      await qc.invalidateQueries({ queryKey: ANFRAGEN_KEY });
      return x;
    }, t("Termine geschickt. Die Mail ist im Postausgang.", "Times sent. The email is queued."));
    if (r !== undefined) onClose();
  };
  return (
    <Dialog open={!!anfrage} onClose={onClose} title={t("Termine schicken?", "Send times?")}>
      {anfrage && (
        <>
          <p>{t(`${anfrage.name} (${anfrage.company}) bekommt eine Mail mit den nächsten freien Zeitfenstern (höchstens sechs) und dem persönlichen Link zur Buchungsseite.`,
            `${anfrage.name} (${anfrage.company}) receives an email with the next open time slots (up to six) and the personal link to the booking page.`)}</p>
          {anfrage.slots_sent_at && <p className="notice">{t(`Zuletzt geschickt ${shortWhen(anfrage.slots_sent_at)}${anfrage.slots_sent_count > 1 ? ` (${anfrage.slots_sent_count}×)` : ""}.`, `Last sent ${shortWhen(anfrage.slots_sent_at)}${anfrage.slots_sent_count > 1 ? ` (${anfrage.slots_sent_count}×)` : ""}.`)}</p>}
          {freieZeiten === 0
            ? <p className="notice">{t("Es gibt gerade keine freien Zeitfenster. Gib zuerst rechts welche frei.", "There are no open slots right now. Open some on the right first.")}</p>
            : <p className="muted">{t(`Frei sind gerade ${freieZeiten} Zeitfenster.`, `${freieZeiten} slots are open right now.`)}</p>}
          {error && <p className="tk-error" role="alert">{error}</p>}
          <div className="row">
            <button type="button" className="primary" disabled={!!busy || freieZeiten === 0} aria-busy={busy === "schicken"} onClick={() => void schicken()}>
              {busy === "schicken" ? t("Wird geschickt …", "Sending …") : t("Termine schicken", "Send times")}
            </button>
            <button type="button" onClick={onClose}>{t("Abbrechen", "Cancel")}</button>
          </div>
        </>
      )}
    </Dialog>
  );
}
